import { useDeviceFingerprint } from './use-device-fingerprint';
import { useIpqs } from './use-ipqs';
import { useVerisoul } from './use-verisoul';
import axios from 'axios';
import { useState, useCallback, useEffect } from 'react';
import { ChangePasswordResponseStatus } from 'shared/enums/auth/change-password-response-status.enum';
import { EmailSendResult } from 'shared/enums/email/email-send-result.enum';
import { ChangePasswordResponse } from 'shared/interfaces/auth/change-password-response-interface';
import { LoginResponse } from 'shared/interfaces/auth/login-response.interface';
import { RequestEmailChangeResponse } from 'shared/interfaces/auth/request-email-change-response.interface';
import { TokenVerificationResponse } from 'shared/interfaces/auth/token-verification-response.interface';
import { auth } from 'src/api/auth/auth';
import axiosInstance, { registerAuthClearFunction } from 'src/api/axios/axios-instance';
import { getStoredReferralCode, clearStoredReferralCode } from 'src/utils/affiliate';
import { pushRegistrationConversionEvents, pushRepeatableDataLayerEvent } from 'src/utils/gtm';
import { GtmEventName } from 'src/utils/gtm-event-name.enum';

const USER_SCOPED_TODAY_PLAN_KEYS = [
  'today-plan:last-response',
  'today-plan:first-seen',
  'today-plan:last-variant',
] as const;

/**
 * Custom hook for managing authentication state and actions.
 * Provides functions to set/clear tokens, login, register, and refresh access tokens.
 */
export function useAuth() {
  const [authState, setAuthState] = useState<{
    accessToken: string | null;
    isAuthenticated: boolean;
  }>({
    accessToken: null, // Initial accessToken state
    isAuthenticated: false, // Initial authentication state
  });
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  const { reinitializeSession, getSessionId } = useVerisoul();
  const { syncDeviceResult } = useIpqs();
  const { syncDeviceFingerprint } = useDeviceFingerprint();

  // Side-effect to update the Authorization header whenever accessToken changes.
  useEffect(() => {
    // Set the Authorization header when the accessToken changes.
    if (authState.accessToken) {
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${authState.accessToken}`;
    }
  }, [authState.accessToken]);

  /**
   * Function to set the access token and mark the user as authenticated.
   * @param accessToken - The new access token to set.
   */
  const setAccessToken = useCallback((accessToken: string) => {
    // Update the authState and set the Authorization header.
    setAuthState({ accessToken, isAuthenticated: true });
    axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    setIsAuthLoading(false);
  }, []);

  /**
   * Function to clear the access token and mark the user as unauthenticated.
   */
  const clearAccessToken = useCallback(() => {
    // Clear the authState and remove the Authorization header.
    setAuthState({ accessToken: null, isAuthenticated: false });
    delete axiosInstance.defaults.headers.common['Authorization'];
    USER_SCOPED_TODAY_PLAN_KEYS.forEach((key) => window.sessionStorage.removeItem(key));
    setIsAuthLoading(false);
  }, []);

  // Register the clearAccessToken function with the axios instance
  useEffect(() => {
    registerAuthClearFunction(clearAccessToken);
  }, [clearAccessToken]);

  /**
   * Function to refresh the access token and update the auth state.
   * Throws an error if the refresh fails.
   */
  const refreshAccessToken = useCallback(async () => {
    // Refresh the access token and update the authState.
    try {
      const { accessToken } = await auth.refreshAccessToken(reinitializeSession); // Attempt to refresh the token.
      setAuthState((prev) => ({
        ...prev,
        accessToken: accessToken,
        isAuthenticated: true,
      }));
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`; // Update axios headers.
      return accessToken;
    } catch (error) {
      // A 401 definitively means there is no usable refresh session. Transient
      // failures should not log out a user who still has an access token.
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        setAuthState({ accessToken: null, isAuthenticated: false });
        delete axiosInstance.defaults.headers.common['Authorization'];
      }
      throw error; // Re-throw the error to be caught by the caller
    }
  }, [reinitializeSession]);

  // Restore the cookie-backed session once for the entire application before
  // any route decides whether to show the dashboard or the landing page.
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const isPendingGoogleRegistration =
      searchParams.has('googleSsoEmail') ||
      (/(^|\/)google\/callback\/?$/.test(window.location.pathname) && searchParams.has('email'));

    // Before consent, the refresh_token cookie holds Google's token, which is
    // not in our refresh-token table. Refreshing here would clear that cookie
    // before /auth/google/create-account can use it.
    if (isPendingGoogleRegistration) {
      setIsAuthLoading(false);
      return;
    }

    let isMounted = true;

    const initializeAuth = async () => {
      const retryDelays = [0, 250, 750];

      for (const retryDelay of retryDelays) {
        if (retryDelay > 0) {
          await new Promise<void>((resolve) => window.setTimeout(resolve, retryDelay));
        }

        try {
          await refreshAccessToken();
          break;
        } catch (error) {
          const isInvalidSession = axios.isAxiosError(error) && error.response?.status === 401;
          const isLastAttempt = retryDelay === retryDelays[retryDelays.length - 1];

          if (isInvalidSession || isLastAttempt) {
            break;
          }
        }
      }

      if (isMounted) {
        setIsAuthLoading(false);
      }
    };

    void initializeAuth();

    return () => {
      isMounted = false;
    };
  }, [refreshAccessToken]);

  /**
   * Function to login a user with email and password.
   * Updates the auth state upon success.
   * @param email - User's email.
   * @param password - User's password.
   * @returns The login response
   */
  const login = async (email: string, password: string): Promise<LoginResponse> => {
    // Login the user and update the authState
    await reinitializeSession();
    syncDeviceResult();
    await syncDeviceFingerprint();
    const verisoulSessionId = await getSessionId();
    const loginResponse = await auth.login(email, password, verisoulSessionId ?? undefined);
    if (loginResponse?.tokens?.accessToken) {
      setAccessToken(loginResponse.tokens.accessToken);
    }
    return loginResponse;
  };

  /**
   * Function to register a new user.
   * The user remains unauthenticated until they verify their email.
   * @param email - User's email.
   * @param password - User's password.
   * @param isConsentChecked - Whether user accepted TOS and privacy policy.
   * @param referralCode - Optional referral code from affiliate link.
   * @param utmParams - UTM parameters for user acquisition tracking.
   */
  const register = async (
    email: string,
    password: string,
    isConsentChecked: boolean,
    referralCode?: string,
    utmParams?: {
      utm_source?: string;
      utm_medium?: string;
      utm_campaign?: string;
      utm_term?: string;
      utm_content?: string;
      utm_id?: string;
      utm_affclickid?: string;
    }
  ) => {
    await reinitializeSession();
    syncDeviceResult();
    await syncDeviceFingerprint();
    const verisoulSessionId = await getSessionId();
    await auth.register(email, password, isConsentChecked, referralCode, utmParams, verisoulSessionId ?? undefined);
    pushRegistrationConversionEvents();
  };

  const logout = async () => {
    await auth.logout();
    clearAccessToken();
    await reinitializeSession();
  };

  const resetPasswordRequest = async (email: string): Promise<EmailSendResult> => {
    pushRepeatableDataLayerEvent(GtmEventName.PasswordResetStart, { source: 'password_reset_request' });
    syncDeviceResult();
    await syncDeviceFingerprint();
    const verisoulSessionId = await getSessionId();
    const response = await auth.resetPasswordRequest(email, verisoulSessionId ?? undefined);
    return response;
  };

  /**
   * Function to change the user's password.
   * Updates the auth state upon success.
   * @param oldPassword - User's old password.
   * @param newPassword - User's new password.
   * @returns The change password response
   */
  const changePassword = async (oldPassword: string, newPassword: string): Promise<ChangePasswordResponse> => {
    // Change the user's password and update the authState.
    syncDeviceResult();
    await syncDeviceFingerprint();
    const verisoulSessionId = await getSessionId();
    const changePasswordResponse = await auth.changePassword(oldPassword, newPassword, verisoulSessionId ?? undefined);
    if (
      changePasswordResponse.status === ChangePasswordResponseStatus.Success &&
      changePasswordResponse.tokens?.accessToken
    ) {
      setAccessToken(changePasswordResponse.tokens.accessToken);
    }
    return changePasswordResponse;
  };

  const emailChangeRequest = async (newEmail: string): Promise<RequestEmailChangeResponse> => {
    syncDeviceResult();
    await syncDeviceFingerprint();
    const verisoulSessionId = await getSessionId();
    const response = await auth.emailChangeRequest(newEmail, verisoulSessionId ?? undefined);
    return response;
  };

  /**
   * Function to create a new Google account in the database.
   * Called after user accepts TOS and privacy policy.
   * Sends stored affiliate referral code so the referrer gets credit when user signed up via affiliate link.
   */
  const createGoogleSsoAccount = async (isConsentChecked: boolean) => {
    const referralCode = getStoredReferralCode();
    syncDeviceResult();
    await syncDeviceFingerprint();
    const verisoulSessionId = await getSessionId();
    const accessToken = await auth.createGoogleSsoAccount(
      isConsentChecked,
      verisoulSessionId ?? undefined,
      referralCode ?? undefined
    );
    if (referralCode) {
      clearStoredReferralCode();
    }
    setAccessToken(accessToken);
    pushRegistrationConversionEvents();
  };

  const verifyEmail = async (token: string): Promise<TokenVerificationResponse> => {
    const emailVerificationResponse = await auth.verifyEmail(token);
    if (emailVerificationResponse?.accessToken) {
      setAccessToken(emailVerificationResponse?.accessToken);
    }
    pushRepeatableDataLayerEvent(GtmEventName.EmailVerified);
    return emailVerificationResponse;
  };

  return {
    ...authState, // Return current authentication state.
    isAuthLoading,
    setAccessToken, // Function to set authentication state.
    clearAccessToken, // Function to clear authentication state.
    refreshAccessToken, // Function to refresh the access token.
    login, // Function to login a user.
    logout, // Function to logout a user.
    register, // Function to register a new user.
    createGoogleSsoAccount, // Function to create a Google account.
    verifyEmail, // Function to verify the user's email.
    changePassword, // Function to change the user's password.
    resetPasswordRequest, // Function to reset the user's password.
    emailChangeRequest, // Function to request an email change.
  };
}
